// MerchPage.js - Pre-order only nonprofit merch store
import React, { useState, useEffect } from 'react';
import Navbar from '../../components/Navbar/Navbar';
import Footer from '../../components/Footer/Footer';
import StickyBottomNav from '../../components/Navbar/StickyBottomNav';
import { CartProvider, useCart } from './PreOrderCartContext';
import PreOrderCart from './PreOrderCart';
import PreOrderCheckout from './PreOrderCheckout';
import SizeChartModal from './SizeChartModal';
import NotificationSystem, { useNotification } from './NotificationSystem';
import { ShoppingCart, AlertCircle, Info, Clock, Package, ChevronLeft, ChevronRight, Heart, Lock } from 'lucide-react';

// Import images
import CarouselPhoto1 from '../../images/Merch/HeroPhoto1.jpg';
import CarouselPhoto2 from '../../images/Merch/HeroPhoto2.jpg';
import CarouselPhoto3 from '../../images/Merch/HeroPhoto3.jpg';
import CarouselPhoto4 from '../../images/Merch/HeroPhoto4.jpg';

// Green product images
import GreenMerch1 from '../../images/Merch/GreenMerch1.jpg';
import GreenMerch2 from '../../images/Merch/GreenMerch2.jpg';
import GreenMerch3 from '../../images/Merch/GreenMerch3.jpg';
import GreenMerch4 from '../../images/Merch/GreenMerch4.jpg';
import GreenMerch5 from '../../images/Merch/GreenMerch5.jpg';
import GreenMerch6 from '../../images/Merch/GreenMerch6.jpg';

// Black product images
import BlackMerch1 from '../../images/Merch/BlackMerch1.jpg';
import BlackMerch2 from '../../images/Merch/BlackMerch2.jpg';
import BlackMerch3 from '../../images/Merch/BlackMerch3.jpg';
import BlackMerch4 from '../../images/Merch/BlackMerch4.jpg';
import BlackMerch5 from '../../images/Merch/BlackMerch5.jpg';
import BlackMerch6 from '../../images/Merch/BlackMerch6.jpg';

// Product data with updated structure
const productData = {
  name: "SFR Oversized T-Shirt",
  price: 30,
  description: "Premium oversized comfort tee supporting refugee communities",
  features: [
    "SFR Logo printed on front and back",
    "Drop shoulder fit",
    "100% Terry Cotton",
    "Machine wash cold",
    "Unisex sizing"
  ],
  availableSizes: ['M', 'L', 'XL', '2XL'],
  sizeChart: {
    'M': '44',
    'L': '46',
    'XL': '48',
    '2XL': '50'
  },
  colors: {
    'Green': {
      colorCode: '#4A5D4F',
      images: [GreenMerch1, GreenMerch2, GreenMerch3, GreenMerch4, GreenMerch5, GreenMerch6]
    },
    'Black': {
      colorCode: '#000000',
      images: [BlackMerch1, BlackMerch2, BlackMerch3, BlackMerch4, BlackMerch5, BlackMerch6]
    }
  }
};

const heroSlides = [
  { id: 1, image: CarouselPhoto1 },
  { id: 2, image: CarouselPhoto2 },
  { id: 3, image: CarouselPhoto3 },
  { id: 4, image: CarouselPhoto4 }
];

// Product Gallery Component
const ProductGallery = ({ images, color }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  
  const nextImage = () => {
    setActiveIndex((prev) => (prev + 1) % images.length);
  };
  
  const prevImage = () => {
    setActiveIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <div className="space-y-4">
      {/* Main Image Display */}
      <div className="relative aspect-[3/4] bg-gray-100 rounded-lg overflow-hidden group">
        <img 
          src={images[activeIndex]}
          alt={`${color} oversized t-shirt view ${activeIndex + 1}`}
          className="w-full h-full object-cover"
        />
        
        {/* Navigation Arrows */}
        <button
          onClick={prevImage}
          className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
          aria-label="Previous image"
        >
          <ChevronLeft size={24} />
        </button>
        
        <button
          onClick={nextImage}
          className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
          aria-label="Next image"
        >
          <ChevronRight size={24} />
        </button>

        {/* Pre-order Badge */}
        <div className="absolute top-4 left-4 bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-bold shadow-lg">
          PRE-ORDER
        </div>
      </div>
      
      {/* Thumbnail Strip */}
      <div className="grid grid-cols-6 gap-2">
        {images.map((img, index) => (
          <button
            key={index}
            onClick={() => setActiveIndex(index)}
            className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${
              activeIndex === index ? 'border-primary ring-2 ring-primary/30' : 'border-gray-200 hover:border-gray-400'
            }`}
          >
            <img 
              src={img}
              alt={`Thumbnail ${index + 1}`}
              className="w-full h-full object-cover"
            />
          </button>
        ))}
      </div>
    </div>
  );
};

// Pre-order Notice Component
const PreOrderNotice = () => (
  <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3 px-6 text-center sticky top-0 z-40">
    <div className="container mx-auto flex items-center justify-center gap-3 flex-wrap">
      <Clock className="animate-pulse" size={20} />
      <span className="font-bold text-lg">PRE-ORDER NOW OPEN</span>
      <span className="text-sm md:text-base">• Expected delivery: 4-6 weeks • Pickup at events only</span>
    </div>
  </div>
);

// Cart Icon Component
const CartIcon = () => {
  const { getTotalItems, setIsCartOpen } = useCart();
  const itemCount = getTotalItems();

  return (
    <button
      onClick={() => setIsCartOpen(true)}
      className="fixed top-20 right-4 z-40 bg-primary text-white p-3 rounded-full shadow-lg hover:scale-110 transition-transform"
      aria-label={`Shopping cart with ${itemCount} items`}
    >
      <ShoppingCart size={24} />
      {itemCount > 0 && (
        <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs w-6 h-6 rounded-full flex items-center justify-center">
          {itemCount}
        </span>
      )}
    </button>
  );
};

// Updated Product Section Component
const ProductShowcase = () => {
  const [selectedColor, setSelectedColor] = useState('Green');
  const [selectedSize, setSelectedSize] = useState('');
  const [showSizeChart, setShowSizeChart] = useState(false);
  
  const { addToCart } = useCart();
  const { showNotification } = useNotification();

  const handleAddToCart = () => {
    if (!selectedSize) {
      showNotification('Please select a size', 'warning');
      return;
    }

    const cartItem = {
      id: `tshirt-${selectedColor.toLowerCase()}`,
      name: `${productData.name} - ${selectedColor}`,
      color: selectedColor,
      size: selectedSize,
      price: productData.price,
      image: productData.colors[selectedColor].images[0],
      isPreOrder: true,
      timestamp: Date.now()
    };

    addToCart(cartItem);
    showNotification('Pre-order item added to cart!', 'success');
  };

  return (
    <section className="py-16 px-6">
      <div className="container mx-auto max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left: Product Gallery */}
          <div>
            <ProductGallery 
              images={productData.colors[selectedColor].images} 
              color={selectedColor}
            />
          </div>
          
          {/* Right: Product Details */}
          <div className="space-y-6">
            {/* Product Title and Price */}
            <div>
              <h1 className="font-display text-3xl md:text-4xl text-secondary mb-2">
                {productData.name}
              </h1>
              <div className="flex items-center gap-4 mb-4">
                <p className="text-3xl font-bold text-primary">${productData.price}</p>
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold">
                  100% to refugees
                </span>
              </div>
              <p className="text-gray-600">{productData.description}</p>
            </div>

            {/* Color Selection */}
            <div>
              <p className="text-sm font-semibold text-gray-700 mb-3">COLOR: {selectedColor}</p>
              <div className="flex gap-3">
                {Object.entries(productData.colors).map(([colorName, colorData]) => (
                  <button
                    key={colorName}
                    onClick={() => setSelectedColor(colorName)}
                    className={`relative w-12 h-12 rounded-full border-2 transition-all ${
                      selectedColor === colorName 
                        ? 'border-primary ring-2 ring-primary/30' 
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                    style={{ backgroundColor: colorData.colorCode }}
                    aria-label={`Select ${colorName} color`}
                  >
                    {selectedColor === colorName && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-3 h-3 bg-white rounded-full"></div>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Size Selection */}
            <div>
              <div className="flex items-center gap-4 mb-3">
                <p className="text-sm font-semibold text-gray-700">SIZE</p>
                <button
                  onClick={() => setShowSizeChart(true)}
                  className="text-sm text-primary underline flex items-center gap-1 hover:text-primary/80"
                >
                  <Info size={14} />
                  Size Guide
                </button>
              </div>
              <div className="grid grid-cols-4 gap-2">
                {productData.availableSizes.map((size) => (
                  <button
                    key={size}
                    onClick={() => setSelectedSize(size)}
                    className={`py-3 px-4 border-2 rounded-lg transition-all font-medium ${
                      selectedSize === size
                        ? 'bg-primary text-white border-primary'
                        : 'border-gray-300 hover:border-primary'
                    }`}
                  >
                    {size}
                  </button>
                ))}
              </div>
              <p className="text-xs text-orange-600 mt-2 font-medium flex items-start gap-1">
                <AlertCircle size={14} className="mt-0.5" />
                Sizes run smaller than standard - consider ordering one size up
              </p>
            </div>

            {/* Product Details */}
            <div className="border-t border-b py-4">
              <p className="text-sm font-semibold text-gray-700 mb-3">PRODUCT DETAILS</p>
              <ul className="space-y-2">
                {productData.features.map((feature, index) => (
                  <li key={index} className="text-sm text-gray-600 flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* 100% Impact Message */}
            <div className="bg-gradient-to-r from-primary/10 to-cta/10 p-4 rounded-lg">
              <div className="flex items-start gap-3">
                <Heart className="text-primary mt-1" size={20} />
                <div>
                  <p className="font-bold text-gray-900">100% Impact Guarantee</p>
                  <p className="text-sm text-gray-700 mt-1">
                    Every dollar from your purchase goes directly to supporting refugee families. 
                    Your fashion choice creates real change in their lives.
                  </p>
                </div>
              </div>
            </div>

            {/* Pre-order Info */}
            <div className="bg-orange-50 border border-orange-200 p-4 rounded-lg">
              <div className="flex items-start gap-3">
                <Package className="text-orange-600 mt-1" size={20} />
                <div>
                  <p className="font-bold text-orange-900">Pre-Order Item</p>
                  <p className="text-sm text-orange-700 mt-1">
                    Expected to ship in 4-6 weeks. You'll be contacted when your order is ready 
                    and given options to pick up at an SFR event.
                  </p>
                </div>
              </div>
            </div>

            {/* Add to Cart Button */}
            <button
              onClick={handleAddToCart}
              className="w-full bg-gradient-to-r from-primary to-cta text-white py-4 rounded-full font-bold text-lg hover:shadow-lg transform hover:scale-[1.02] transition-all"
            >
              Add to Pre-Order Cart
            </button>

            {/* Payment Security Note */}
            <p className="text-center text-xs text-gray-500 flex items-center justify-center gap-2">
              <Lock size={14} />
              Secure checkout powered by Stripe
            </p>
          </div>
        </div>
      </div>
      
      {/* Size Chart Modal */}
      {showSizeChart && (
        <SizeChartModal onClose={() => setShowSizeChart(false)} />
      )}
    </section>
  );
};

// Main Component
const MerchPage = () => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [showCheckout, setShowCheckout] = useState(false);
  const { showNotification } = useNotification();

  // Check for Stripe return and show notification
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const status = urlParams.get('status');
    const orderId = urlParams.get('orderId');

    if (status === 'success' && orderId) {
      // Show success notification for 10 seconds
      showNotification(
        `Order ${orderId} confirmed! ✨ You'll receive an email receipt shortly. We'll contact you when your items are ready for pickup (4-6 weeks).`,
        'success',
        { timeout: 10000 } // 10 seconds
      );
      
      // Clean up URL to remove query parameters
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);
    } else if (status === 'cancelled') {
      // Show cancelled notification
      showNotification(
        'Order cancelled. Your items are still in your cart if you want to try again.',
        'info',
        { timeout: 5000 }
      );
      
      // Clean up URL
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);
    }
  }, [showNotification]);

  // Check for Stripe return and show notification
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const status = urlParams.get('status');
    const orderId = urlParams.get('orderId');

    if (status === 'success' && orderId) {
      // Show success notification for 10 seconds
      showNotification(
        `Order ${orderId} confirmed! ✨ You'll receive an email receipt shortly. We'll contact you when your items are ready for pickup (4-6 weeks).`,
        'success',
        { timeout: 10000 } // 10 seconds
      );
      
      // Clean up URL to remove query parameters
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);
    } else if (status === 'cancelled') {
      // Show cancelled notification
      showNotification(
        'Order cancelled. Your items are still in your cart if you want to try again.',
        'info',
        { timeout: 5000 }
      );
      
      // Clean up URL
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);
    }
  }, [showNotification]);

  // Auto-rotate carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % heroSlides.length);
    }, 7000);
    return () => clearInterval(timer);
  }, []);

  // Scroll progress
  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (window.scrollY / totalHeight) * 100;
      setScrollProgress(progress);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLinkClick = (e, target) => {
    e.preventDefault();
    if (target === 'scroll-to-products') {
      const el = document.getElementById('product-showcase');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      window.location.href = target;
    }
  };

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      {/* Progress Bar */}
      <div className="fixed top-0 left-0 right-0 h-1 bg-gray-200 z-50">
        <div 
          className="h-full bg-gradient-to-r from-primary to-cta transition-all duration-300"
          style={{ width: `${scrollProgress}%` }}
        />
      </div>

      <Navbar />
      
      {/* Pre-order Notice */}
      <PreOrderNotice />

      {/* Cart Icon */}
      <CartIcon />

      {/* Cart Sidebar */}
      <PreOrderCart onCheckout={() => setShowCheckout(true)} />

      {/* Checkout Modal */}
      {showCheckout && (
        <PreOrderCheckout onClose={() => setShowCheckout(false)} />
      )}

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Carousel Background */}
        <div className="absolute inset-0">
          {heroSlides.map((slide, index) => (
            <div
              key={slide.id}
              className={`absolute inset-0 transition-opacity duration-1500 ${
                activeSlide === index ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img 
                src={slide.image}
                alt={`Background ${index + 1}`}
                className="w-full h-full object-cover"
              />
            </div>
          ))}
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-secondary/85 via-primary/75 to-cta/85"></div>
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          ></div>
        </div>

        {/* PRE-ORDER badge */}
        <div className="absolute top-8 left-8 z-20">
          <span className="inline-block bg-orange-500 text-white px-4 py-2 rounded-full font-bold shadow-lg">
            PRE-ORDER
          </span>
        </div>
        
        {/* Carousel Indicators */}
        <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 flex gap-2 z-20">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => setActiveSlide(index)}
              className={`h-2 transition-all duration-300 rounded-full ${
                activeSlide === index ? 'w-12 bg-white' : 'w-2 bg-white/50 hover:bg-white/70'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
        
        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
          <h1 className="font-display text-white text-6xl md:text-7xl lg:text-8xl mb-6 leading-tight">
            Fashion
            <span className="block text-4xl md:text-5xl lg:text-6xl mt-2 text-white/90">
              that Changes Lives
            </span>
          </h1>
          <p className="text-white/90 text-xl md:text-2xl mb-8 max-w-3xl mx-auto font-body">
            Every purchase writes a new chapter in a refugee's story of hope and resilience
          </p>
          <div className="mb-8">
            <p className="text-white text-lg font-bold">
              <Heart className="inline-block mr-2" size={20} />
              100% of profits go directly to refugee families
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <button 
              onClick={(e) => handleLinkClick(e, "scroll-to-products")}
              className="group relative bg-white text-secondary px-10 py-5 rounded-full font-bold text-lg overflow-hidden transition-all hover:shadow-2xl"
            >
              <span className="relative z-10">Shop Collection</span>
              <div className="absolute inset-0 bg-cta transform -translate-x-full group-hover:translate-x-0 transition-transform duration-500"></div>
            </button>
            <button 
              onClick={(e) => handleLinkClick(e, "/#our-story")}
              className="border-2 border-white text-white px-10 py-5 rounded-full font-bold text-lg hover:bg-white hover:text-secondary transition-all backdrop-blur-sm"
            >
              Our Story
            </button>
          </div>
        </div>
        
        {/* Animated scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2">
          <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white rounded-full mt-2 animate-bounce"></div>
          </div>
        </div>
      </section>

      {/* Important Notice Section */}
      <section className="py-16 bg-orange-50">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-display text-3xl text-secondary mb-6">Important Pre-Order Information</h2>
            <div className="space-y-4 text-left bg-white p-6 rounded-lg shadow">
              <div className="flex items-start gap-3">
                <span className="text-orange-500 font-bold">📦</span>
                <p><strong>Pre-Order Only:</strong> All items are made to order. Expected production time is 4-6 weeks.</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-orange-500 font-bold">💳</span>
                <p><strong>Payment Required:</strong> Full payment is required at time of order to secure your items.</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-orange-500 font-bold">📍</span>
                <p><strong>Pickup Only:</strong> You'll be contacted when your order is ready and given options to pick up at an SFR event.</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-orange-500 font-bold">❌</span>
                <p><strong>No Refunds:</strong> All pre-orders are final sale. Please check size chart carefully.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Product Showcase */}
      <div id="product-showcase">
        <ProductShowcase />
      </div>

      <Footer />
      <StickyBottomNav />
    </div>
  );
};

// Export with providers
export default function MerchPageWithProviders() {
  return (
    <NotificationSystem>
      <CartProvider>
        <MerchPage />
      </CartProvider>
    </NotificationSystem>
  );
}